import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { MessagesLandingMenuItem } from './types'
import { useMenuOperations } from './useMenuOperations'
import { MenuToolbar } from './MenuToolbar'
import { MenuItemsRenderer } from './MenuItemsRenderer'
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { useState } from 'react'
import { Badge } from '@/components/shadcn/badge'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { IconHelpQuestionOutline } from '@icons/IconHelpQuestionOutline'
import { IconDragHandleOutline } from '@icons/IconDragHandleOutline'

type DropPosition = 'before' | 'after' | 'inside'
type ActiveDropTarget = { id: string; position: DropPosition } | null
type DropTarget = { id: string; position: DropPosition }

// Main component
export function MenuTable({ data: initialData }: { data: MessagesLandingMenuItem[] }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const [activeDropTarget, setActiveDropTarget] = useState<ActiveDropTarget>(null)
  const [draggedItem, setDraggedItem] = useState<MessagesLandingMenuItem | null>(null)
  const {
    menuItems,
    setMenuItems,
    expandedItems,
    isExpanded,
    hasChanged,
    toggleItem,
    toggleExpandCollapse,
    addChildItem,
    deleteItem,
    editItem,
    addRootItem,
    moveItem,
    handleSave,
    removedPageIdsPendingConfirmation,
    isSaving,
    isLoadingCustomMenu,
    isDeletingCustomMenu,
    isCustomMenu,
    selectedMenuName,
    menuOptions,
    customMenuNames,
    handleMenuSelectionChange,
    handleCustomMenuCreated,
    getCustomMenuApiPathParam,
    copyMenuLayoutToClipboard,
    pasteMenuLayoutFromClipboard,
    handleDeleteCustomMenu,
    confirmSaveAndDeleteRemovedPages,
    saveWithoutDeletingRemovedPages,
    setRemovedPageIdsPendingConfirmation,
    initialData: originalData,
  } = useMenuOperations(initialData)

  const handleDragStart = (event: DragStartEvent) => {
    setDraggedItem(getItemById(menuItems, String(event.active.id)))
  }

  const handleDragMove = (event: DragMoveEvent) => {
    const overId = event.over?.id ? String(event.over.id) : null

    if (!overId) {
      setActiveDropTarget(null)
      return
    }

    const position = getDropPosition(event)
    const dropTarget = position ? getResolvedDropTarget(menuItems, expandedItems, overId, position) : null

    setActiveDropTarget(dropTarget)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const sourceId = String(event.active.id)
    const overId = event.over?.id ? String(event.over.id) : null
    setActiveDropTarget(null)
    setDraggedItem(null)

    if (!overId) {
      return
    }

    const position = getDropPosition(event)

    if (!position) {
      return
    }

    const dropTarget = getResolvedDropTarget(menuItems, expandedItems, overId, position)

    moveItem(sourceId, dropTarget)
  }

  return (
    <div className="w-full">
      <MenuToolbar
        isExpanded={isExpanded}
        hasChanged={hasChanged}
        toggleExpandCollapse={toggleExpandCollapse}
        addRootItem={addRootItem}
        handleSave={handleSave}
        resetMenuItems={() => setMenuItems(originalData)}
        selectedMenuName={selectedMenuName}
        menuOptions={menuOptions}
        customMenuNames={customMenuNames}
        onMenuSelectionChange={handleMenuSelectionChange}
        onCustomMenuCreated={handleCustomMenuCreated}
        getCustomMenuApiPathParam={getCustomMenuApiPathParam}
        isCustomMenu={isCustomMenu}
        onDeleteCustomMenu={handleDeleteCustomMenu}
        isDeletingCustomMenu={isDeletingCustomMenu}
        onCopyMenuLayoutToClipboard={copyMenuLayoutToClipboard}
        onPasteMenuLayoutFromClipboard={pasteMenuLayoutFromClipboard}
      />

      <div className="overflow-x-hidden bg-transparent [&>div]:overflow-x-hidden">
        {isLoadingCustomMenu ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading menu...</p>
        ) : (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            setActiveDropTarget(null)
            setDraggedItem(null)
          }}
        >
          <Table className="table-fixed">
            <TableHeader className="bg-transparent [&_tr]:border-0">
              <TableRow className="border-0 hover:bg-transparent">
                <TableHead className="w-12 border-0 font-bold" />
                <TableHead className="w-12 border-0 font-bold">Icon</TableHead>
                <TableHead className="border-0 font-bold">Menu Item</TableHead>
                <TableHead className="border-0 font-bold">Page ID</TableHead>
                <TableHead className="border-0 font-bold">Roles</TableHead>
                <TableHead className="border-0 font-bold" />
              </TableRow>
            </TableHeader>
            <TableBody className="[&_tr:last-child]:border-0">
              <MenuItemsRenderer
                menuItems={menuItems}
                expandedItems={expandedItems}
                toggleItem={toggleItem}
                addChildItem={addChildItem}
                deleteItem={deleteItem}
                editItem={editItem}
                activeDropTarget={activeDropTarget}
              />
            </TableBody>
          </Table>
          <DragOverlay dropAnimation={null}>
            {draggedItem ? <MenuItemDragOverlay item={draggedItem} /> : null}
          </DragOverlay>
        </DndContext>
        )}
      </div>
      <Dialog
        open={removedPageIdsPendingConfirmation.length > 0}
        onOpenChange={(open) => {
          if (!open && !isSaving) {
            setRemovedPageIdsPendingConfirmation([])
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete removed pages?</DialogTitle>
            <DialogDescription>
              These pages are no longer linked from the menu. You can delete them now, or save the menu and keep the
              pages available for later use.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-48 overflow-auto rounded-md border bg-background p-3">
            <ul className="space-y-1 text-sm">
              {removedPageIdsPendingConfirmation.map((pageId) => (
                <li key={pageId} className="font-mono">
                  {pageId}
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={saveWithoutDeletingRemovedPages} disabled={isSaving}>
              Keep pages
            </Button>
            <Button variant="destructive" onClick={confirmSaveAndDeleteRemovedPages} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Delete pages and save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function MenuItemDragOverlay({ item }: { item: MessagesLandingMenuItem }) {
  return (
    <div className="flex min-w-[32rem] items-center gap-3 rounded-md bg-primary/10 px-3 py-2 text-sm shadow-md">
      <IconDragHandleOutline className="size-4 shrink-0 text-muted-foreground" />
      {item.icon_name ? (
        <DynamicIcon
          iconName={item.icon_name}
          className="size-4 shrink-0"
          fallback={<IconHelpQuestionOutline className="size-4 shrink-0" />}
        />
      ) : (
        <IconHelpQuestionOutline className="size-4 shrink-0" />
      )}
      <span className="min-w-0 flex-1 truncate font-medium">{item.name}</span>
      <span className="max-w-40 truncate text-muted-foreground">{item.page_id}</span>
      <div className="flex max-w-48 shrink-0 flex-wrap justify-end gap-1">
        {item.roles.slice(0, 2).map((role) => (
          <Badge key={role} variant="outline" className="bg-background/40 py-0">
            {role}
          </Badge>
        ))}
        {item.roles.length > 2 && (
          <Badge variant="outline" className="bg-background/40 py-0">
            +{item.roles.length - 2}
          </Badge>
        )}
      </div>
    </div>
  )
}

function getItemById(items: MessagesLandingMenuItem[], id: string) {
  const path = id.split('-').map(Number)
  let currentItems = items

  for (let index = 0; index < path.length; index++) {
    const item = currentItems[path[index]]

    if (!item) {
      return null
    }

    if (index === path.length - 1) {
      return item
    }

    currentItems = item.items ?? []
  }

  return null
}

function getResolvedDropTarget(
  items: MessagesLandingMenuItem[],
  expandedItems: string[],
  id: string,
  position: DropPosition
): DropTarget {
  const item = getItemById(items, id)

  if (position === 'after' && expandedItems.includes(id) && item?.items?.length) {
    return { id: `${id}-0`, position: 'before' }
  }

  return { id, position }
}

function getDropPosition(event: DragEndEvent | DragMoveEvent): DropPosition | undefined {
  const overRect = event.over?.rect
  const translatedRect = event.active.rect.current.translated

  if (!overRect || !translatedRect) {
    return undefined
  }

  const dropCenterY = translatedRect.top + translatedRect.height / 2
  const topZoneEnd = overRect.top + overRect.height / 3
  const bottomZoneStart = overRect.bottom - overRect.height / 3

  if (dropCenterY < topZoneEnd) {
    return 'before'
  }

  if (dropCenterY > bottomZoneStart) {
    return 'after'
  }

  return 'inside'
}
